import React from "react";
import {
  TextNode as RPDFCTextNode,
  TextNodeProps as RPDFCTextNodeProps,
} from "@surge-global-engineering/react-pdf-components";
import { StyleSheet } from "@surge-global-engineering/rpdf-renderer";
import { PdfSlateTextNode, PdfTheme } from "../types";

interface TextNodeProps {
  textNode: PdfSlateTextNode;
  theme: PdfTheme;
  parentType?: string;
  isIndented?: boolean;
  canJustifyText?: boolean;
  canHaveSoftBreaks?: boolean;
  isHangingIndent?: boolean;
}

const TextNode: React.FC<TextNodeProps> = ({
  textNode,
  theme,
  parentType,
  isIndented,
  canJustifyText = true,
  canHaveSoftBreaks = false,
  isHangingIndent = false
}) => {
  const {
    bold,
    italic,
    underline,
    monospace,
    smallcaps,
    subscript,
    superscript,
    strikethrough,
    sansserif,
    text
  } = textNode;
  const { paragraph, baseFontSize, printBaseFont, headings, footnoteFontSize } = theme.properties;
  const { justify, paragraphSpacing } = paragraph;

  const isEndnote = parentType === "endnote";

  const fontSizeValue =
    parentType === "h2"
      ? baseFontSize * headings.h2.size
      : parentType === "h3"
      ? baseFontSize * headings.h3.size
      : parentType === "h4"
      ? baseFontSize * headings.h4.size
      : parentType === "h5"
      ? baseFontSize * headings.h5.size
      : parentType === "h6"
      ? baseFontSize * headings.h6.size
      : parentType === "footnote" 
      ? baseFontSize * footnoteFontSize
      : parentType === "endnote-chapter" 
      ? baseFontSize * footnoteFontSize
      : baseFontSize;
  
      

  const boldValue = parentType === "h2" ? true : bold;

  const indentValue = parentType === "endnote-chapter" ? false : isIndented;

  const superscriptValue = isEndnote ? true : superscript;

  const getTextContent = () => {
    if (isEndnote) {
      return [text];
    }
     /* if there is no content in the text node, we add a newline character, to get an empty line rendered */
     const content = text || "\n";
     
     if (canHaveSoftBreaks) {
       // split keeping the newline so it would create a para
       return content
         .split("\n")
         .map((element, index, elements) => {
           if (index + 1 !== elements.length) {
             return element.concat("\n");
           }
           return element;
         })
         .filter((element) => !!element);
     }

     return [content];
  };

  const getTextIndent = () => {
    if(isHangingIndent) {
      return `-${theme.properties.hangingIndent}cm`;
    }else {
      if (!indentValue) return "";

    if (["align_center", "align_right", "h2", "h3", "h4", "h5", "h6"].includes(parentType || "")) {
      return "";
    }

      // Do not indent paragraphs if there is a new line character in index 0 in the text.
      if (text && text[0] === "\n") {
        return "";
      }
      return `${theme.properties.hangingIndent}cm`;
    }
  };

  const textAlign =
    (justify && !["align_center", "align_right"].includes(parentType || "")) && canJustifyText
      ? "justify"
      : undefined;

  const getFontFamily = (parentType : string) : string => {

    if(parentType === "text_messages") {
      return "SourceSansPro";
    }

    if(headings[parentType].font !== "Default"){
      return headings[parentType].font;
    }
    return printBaseFont;
  };

  const props: RPDFCTextNodeProps = {
    bold: boldValue,
    italic: italic,
    underline: underline,
    fontSize: fontSizeValue,
    lineHeight: parentType === "text_messages" ? 0 : paragraphSpacing,
    smallCaps: smallcaps,
    subscript: subscript,
    superscript: superscriptValue,
    strikeThrough: strikethrough,
  };

  const styles = StyleSheet.create({
    main: {
      fontFamily:
        parentType && ["h2", "h3", "h4", "h5", "h6", "text_messages"].includes(parentType)
          ? getFontFamily(parentType)
          : printBaseFont,
      textIndent: getTextIndent(),
      textAlign,
    },
  });

  const getExtraTextStyles = () => {
    if (sansserif) {
      return baseStyles.sansserif;
    }
    if (monospace) {
      return baseStyles.monospace;
    }
    return {};
  };

  return (
    <>
      {getTextContent().map((text, index) => (
        <RPDFCTextNode
          key={`${textNode.id}_${index}`}
          {...props}
          style={[
            styles.main,
            getExtraTextStyles(),
            index !== 0 ? { textIndent: 0 } : {},
          ]}
        >
          {text}
        </RPDFCTextNode>
      ))}
    </>
  );
};

const baseStyles = StyleSheet.create({
  monospace: {
    fontFamily: "CourierPrime"
  },
  sansserif: {
    fontFamily: "PTSans"
  }
});

export default TextNode;
